import { Flyout } from '../components/global-nav/flyout'
import { GlobalNav } from '../components/global-nav/global-nav'
import { INITIAL_RENDER } from '../events'

/**
 * Orchestrates opening and closing of global nav flyouts.
 */
export class FlyoutService {
	public allFlyoutsLoaded = new Promise<boolean>(resolve => {
		this._resolver = resolve
	})

	private _flyoutLoadedPromises: Promise<any>[] = []
	private _resolver: (value: unknown) => void

	private _flyouts = new Map<string, Flyout>()

	constructor(nav: GlobalNav) {
		this._addEscapeListener()
		nav.addEventListener(INITIAL_RENDER, () => this._initFlyoutsLoadedPromise(nav), { once: true })
	}

	private _addEscapeListener() {
		document.addEventListener('keyup', e => this._escapeListenerHandler(e), true)
	}

	private _escapeListenerHandler(e: KeyboardEvent) {
		const disclosureOpen = document.querySelector('.gb-disclosure-open')
		if (e.key === 'Escape') {
			if (!disclosureOpen) {
				this.closeOpenFlyout(false, true)
				e.preventDefault()
			} else {
				window.gbpe.overlay.setZHeight(122)
			}
		}
	}

	private _initFlyoutsLoadedPromise(nav: GlobalNav) {
		const numberOfFlyouts = nav.shadowRoot.querySelectorAll('gb-flyout').length
		// This is the simplest way I could think of to wait for an unknown amount of flyouts to render.
		// They all load super fast but it's still best to wait until the array is full to avoid race conditions.
		const interval = setInterval(async () => {
			if (this._flyoutLoadedPromises.length === numberOfFlyouts) {
				await Promise.all(this._flyoutLoadedPromises)
				this._resolver(true)
				clearInterval(interval)
			}
		}, 50)
	}

	private get _openFlyout(): Flyout | undefined {
		// Should only ever be 2 open flyouts at most
		const openFlyouts = Array.from(this._flyouts.values()).filter(f => f.flyoutOpen)
		if (openFlyouts.length > 1) {
			return openFlyouts[1]
		} else {
			return openFlyouts[0]
		}
	}

	/**
	 * Returns true if a flyout is open, false otherwise.
	 */
	public get flyoutOpen(): boolean {
		return !!this._openFlyout
	}

	/**
	 * Returns true if a flyout is open, false otherwise.
	 */
	public get flyoutOpening(): Flyout {
		return Array.from(this._flyouts.values()).find(f => f.flyoutOpening)
	}

	/**
	 * Closes the open flyout if one is open.
	 * @param triggeredByOverlayClose Indicates to the flyout if the close was triggered by the overlay being clicked.
	 * @returns The ID of the flyout that was closed, if no flyout was closed returns undefined.
	 */
	public closeOpenFlyout(triggeredByOverlayClose = false, triggeredByEscape = false): string | undefined {
		const openFlyout = this._openFlyout
		if (openFlyout) {
			openFlyout.toggleFlyout(triggeredByOverlayClose, true, triggeredByEscape)
			return openFlyout.flyoutId
		}
	}

	/**
	 * Adds a flyout to the `FlyoutService` to manage closing
	 * of it if it's open when a different flyout opens.
	 *
	 * @param flyout The flyout to add to the service.
	 * @param loaded Promise that resolves when the flyout has rendered.
	 */
	public addFlyout(flyout: Flyout, loaded: Promise<boolean>): void {
		this._flyouts.set(flyout.flyoutId, flyout)
		this._flyoutLoadedPromises.push(loaded)
	}
}
